/*** IMPORTS FROM imports-loader ***/
var define = false;

"use strict";

var ko = require("knockout");
var isBrowser = require("targetenv").isBrowser;

/**
 * Applies a validation error to an observable at the specified viewmodel path.
 *
 * Walks up the path, starting at the end, looking for
 * a "setErrorMessage" function. Once found, the function
 * will be passed the error message and the remaining path
 * component as parameters.
 *
 * @param viewModel
 *     Viewmodel that contains the target observable path.
 * @param path
 *     Path to the observable. All indexers in the path are
 *     assumed to be observables and will be evaluated prior
 *     to indexing. E.g., foo[1].bar["b"] will be evaluated
 *     as foo()[1].bar()["b"].
 * @param message
 *     Error message to set on the property.
 */
function applyErrorMessage(viewModel, path, message) {
    var i = path.length;
    var target, remainder;
    var resolveFn, resolvedItem;

    while (i >= 0) {
        target = path.slice(0, i);
        remainder = path.slice(path[i] === "." ? i + 1 : i); // strip leading "."

        /*jslint evil: true */// inspired by knockout binding evaluation
        resolveFn = new Function("viewModel", "with (viewModel) { return (".concat(target.replace(/\[/g, "()["), "); }"));
        /*jslint evil: false */

        resolvedItem = resolveFn(viewModel);
        if (resolvedItem && typeof resolvedItem.setErrorMessage === "function") {
            resolvedItem.setErrorMessage(message, remainder);
            return;
        }

        // current split point didn't yield a "setErrorMessage" handler,
        // move split point to parent (if any)
        i = Math.max(
            path.lastIndexOf(".", i - 1),
            path.lastIndexOf("[", i - 1)
        );
    }

    if (typeof viewModel.setErrorMessage === "function") {
        viewModel.setErrorMessage(message, path);
    } else if (isBrowser && console.warn) {
        console.warn("Unable to set validation error for:", path);
    }
}


/**
 * Applies specified validation errors to the specified viewmodel.
 *
 * Expects validation errors to contain a map, keys being
 * paths to "viewModel" fields/properties, values being
 * error messages to set.
 */
function applyValidationErrors(viewModel, validationErrors) {
    if (validationErrors) {
        delete validationErrors.master;

        var path;
        for (path in validationErrors) {
            if (validationErrors.hasOwnProperty(path)) {
                applyErrorMessage(viewModel, path, validationErrors[path]);
            }
        }
    }
}


/**
 * Counts number of properties with "isValid" property set to "false" (single level, no recursion).
 *
 * This method is directly compatible with PliObservables.
 * Instead of using this method directly, it is recommended
 * that you create a pure computed observable using
 * "createErrorsCountObservable" from this module.
 */
function computeErrorsCount(viewModel) {
    var k, o, sum = 0;
    for (k in viewModel) {
        if (viewModel.hasOwnProperty(k) && k !== "extras" && k !== "id") {
            o = viewModel[k];
            if (o && ko.unwrap(o.isValid) === false) { // explicit check for "false", do not replace with "truthy" test; notice that we subscribe to all "isValid" observables we can find
                sum += 1;
            }
        }
    }
    return sum;
}


/**
 * Creates a computed observable that determines how many properties of the specified viewmodel have a validation error (single level, no recursion).
 *
 * If "isViewModelInitializing" is specified, errors count will be zero
 * while the initializing flag remains true.
 */
function createErrorsCountObservable(viewModel, isViewModelInitializing) {
    return ko.pureComputed(function computeErrorsCountObservable() {
        return (ko.unwrap(isViewModelInitializing)
            ? 0
            : computeErrorsCount(viewModel));
    });
}


/**
 * Initializes a viewmodel state observable with a subscription to state changes.
 */
function createViewModelState(initialState) {
    var state = ko.observable(initialState || "init");

    state.isInitializing = ko.observable(true);
    state.isReady = ko.observable(false);
    state.isBusy = ko.observable(false);
    state.isLoaded = ko.observable(false); // will switch to true when initial load is over (successfuly or not); used to control sign-on form visibility
    state.subscribe(function viewModelStateChanged(stateValue) {
        if (stateValue) {
            if (stateValue.indexOf("ready") === 0) {
                this.isInitializing(false);
                this.isReady(true);
                this.isBusy(false);
                this.isLoaded(true);
                return;
            }
            if (stateValue.indexOf("busy") === 0) {
                this.isInitializing(false);
                this.isReady(false);
                this.isBusy(true);
                this.isLoaded(true);
                return;
            }
            if (stateValue.indexOf("init") === 0) {
                this.isInitializing(true);
                this.isReady(false);
                this.isBusy(false);
                this.isLoaded(stateValue === "init.visible.failed");
                return;
            }
        }
        this.isInitializing(false);
        this.isReady(false);
        this.isBusy(false);
        this.isLoaded(false);
    }, state);

    return state;
}


/**
 * Recursively traverses the object/observable properties and arrays and calls "validate" method" (if available).
 *
 * Difference between computeErrorsCount and validateDeep
 * is that validateDeep will activate delayed behaviours
 * (e.g. required) and is recursive.
 *
 * @returns {boolean}
 *     Returns "true" if the specified object is valid (i.e. all
 *     child objects are also valid), "false" otherwise.
 */
function validateDeep(o) {
    if (o && (typeof o === "object" || ko.isObservable(o))) { // validate only observable and object
        if (o.validate) {
            if (typeof o.validate !== "function") {
                throw new Error("validate must be a function");
            }
            return o.validate();
        }

        if (ko.isObservable(o) && o.indexOf) { // (naive detection of observableArray)
            return validateDeep(o()); // recursive call; in case of observableArray we want to validate each item in the array, thus "o()"
        }

        if (typeof o === "object") {
            // in case of object (includes arrays) we want to validate each property of the object except "extras"
            var k, isValid = true;
            for (k in o) {
                if (o.hasOwnProperty(k)) {
                    if (k !== "extras" && !validateDeep(o[k])) { // recursive call
                        isValid = false; // do not exit early to activate all delayed behaviours
                    }
                }
            }
            return isValid;
        }
    }

    return true;
}


module.exports = {
    applyValidationErrors: applyValidationErrors,
    computeErrorsCount: computeErrorsCount,
    createErrorsCountObservable: createErrorsCountObservable,
    createViewModelState: createViewModelState,
    validateDeep: validateDeep
};




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-viewmodel/shared.js
// module id = 469
// module chunks = 49